// Central shape + defaults for every src/environments/environment.*.ts file.
//
// Why this exists: the per-environment files used to be independent, untyped
// object literals kept in sync by hand. Adding a flag meant editing every file,
// and any file that missed one silently fell back to `undefined` (falsy) at
// runtime with no error. Typing every `environment` as `AppEnvironment` turns a
// missing/extra key into a compile error, and `CLIENT_FLAG_DEFAULTS` means a new
// per-client flag is declared once here instead of copied into every file.
//
// A specific client is added as its own environment.<client>.ts that spreads
// CLIENT_FLAG_DEFAULTS and overrides only the flags that client changes.

export interface BrandConfig {
  logoUrl: string;
  headerLogoUrl: string;
  chatLogoUrl: string;
  faviconUrl: string;
  appName: string;
}

/**
 * Per-client UI customisation flags. The defaults in CLIENT_FLAG_DEFAULTS are the
 * standard experience; a client environment file spreads those defaults and
 * overrides only what differs.
 */
export interface ClientCustomizationFlags {
  // Candidate
  showDeviceCompatibilityCheck: boolean;
  showFaceCapture: boolean;
  showScreenShare: boolean;
  showMobileFeedCapture: boolean;
  showVerifySurroundings: boolean;
  showAudioCheck: boolean;
  showCameraCheck: boolean;
  showCandidatePhone: boolean;
  emailOnlyAuth: boolean;
  hideCandidateResult: boolean;
  /** Proctoring instructions on the rules pages, and the feeds/media on the HR report. */
  showProctoringData: boolean;

  // Interview Bot
  /** Interview bot: local camera tile + the camera-permission gate. */
  showBotVideo: boolean;
  /** Interview bot: the candidate-facing microphone mute button. */
  allowBotMicMute: boolean;
  /** Interview bot: the candidate can type an answer instead of speaking it. */
  allowBotTextChat: boolean;
  /** Interview bot: keep the candidate's name/email out of what the bot is told. */
  maskCandidateIdentityInBot: boolean;
  /** Interview bot: let Vapi record and store the call media. */
  storeBotCallRecording: boolean;
  campusBot: boolean;

  // Admin: assessment creation
  /** Add Section: false hides the Interview section type and locks Question Reasoning Explanation off. */
  allowVideoQuestions: boolean;
}

export interface AppEnvironment extends ClientCustomizationFlags {
  onlyOfficeServerUrl: string;
  production: boolean;
  baseUrl: string;
  baseUrlws: string;
  videoFeedApi: string;
  videoMergeFeed: string;
  gpuProctoringUrl: string;
  gpuQueueStatusUrl: string;
  videoProctoringMachineId: string;
  VAPI_INTERVIEW_AGENT_ASSISTANT_ID: string;
  VAPI_INTERVIEW_AGENT_LATERAL_ASSISTANT_ID: string;
  VAPI_INTERVIEW_AGENT_IMAGINEER_ASSISTANT_ID: string;
  VAPI_INTERVIEW_AGENT_PRACTICE_ASSISTANT_ID: string;
  DOWNLOADAPP: string;
  DESKTOP_IQIGAI_LITE: boolean;
  STATIC_OTP: number;
  deployedClient: string;
  appUrl: string;
  enableSentry: boolean;
  brand: BrandConfig;
}

// Default (iqigai) branding. A client environment file either uses this as-is or
// spreads it and overrides only what differs, e.g.
// `brand: { ...BRAND_DEFAULTS, appName: 'Linde' }`.
export const BRAND_DEFAULTS: BrandConfig = {
  logoUrl: '/assets/images/iqigaiAiLogo.svg',
  headerLogoUrl: '/assets/images/logo_new_header.svg',
  chatLogoUrl: '/assets/images/iqigaiV2Logo.svg',
  faviconUrl: 'favicon.ico',
  appName: 'iqigai',
};

// Standard defaults. A client environment file spreads this and overrides only
// the flags that differ for that client.
export const CLIENT_FLAG_DEFAULTS: ClientCustomizationFlags = {
  // Candidate
  showDeviceCompatibilityCheck: true,
  showFaceCapture: true,
  showScreenShare: true,
  showMobileFeedCapture: true,
  showVerifySurroundings: true,
  showAudioCheck: true,
  showCameraCheck: true,
  showCandidatePhone: true,
  emailOnlyAuth: false,
  hideCandidateResult: false,
  showProctoringData: true,

  // Interview Bot
  showBotVideo: true,
  allowBotMicMute: true,
  allowBotTextChat: false,
  maskCandidateIdentityInBot: false,
  storeBotCallRecording: true,
  campusBot: true,

  // Admin: assessment creation
  allowVideoQuestions: true,
};
